'use client'

import {
  Activity,
  AppWindow,
  BarChart3,
  Bot,
  Boxes,
  Building2,
  CircleGauge,
  CloudCog,
  FolderGit2,
  FolderKanban,
  Gauge,
  Globe2,
  KeyRound,
  Network,
  ShieldCheck,
  Settings2,
  Shield,
  UserRound,
  UsersRound,
  Workflow,
} from 'lucide-react'
import { subscribeNavigation } from '@go-beyond/react/browser'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { SidebarLayout, type SidebarItem } from './ui/sidebar.js'

export type PortalShellData = {
  organizationName: string
  projects: Array<{ slug: string; name: string; capabilities: string[] }>
  teams: Array<{ id: string; name: string }>
  canViewSettings: boolean
  settingsHref: string
  contextKind: 'workspace' | 'organization' | 'project' | 'team' | 'member' | 'none'
  isProject: boolean
  isTeam: boolean
  isMember: boolean
  contextId: string
  contextName: string
  canManageContext: boolean
  isCurrentMember: boolean
}

const organizationItems: SidebarItem[] = [
  { label: 'Projects', href: '/', icon: FolderKanban },
  { label: 'Deployments', href: '/deployments', icon: Activity },
  { label: 'Usage', href: '/usage', icon: BarChart3 },
  { label: 'Teams', href: '/organization/teams', icon: Network, match: 'prefix' },
  { label: 'Members', href: '/organization/members', icon: UsersRound, match: 'prefix' },
  { label: 'General', href: '/organization', icon: Building2, sectionLabel: 'Settings' },
  { label: 'Connections', href: '/organization/connections', icon: Boxes, match: 'prefix' },
  { label: 'Domains', href: '/organization/domains', icon: Globe2, match: 'prefix' },
  { label: 'Advanced', href: '/organization/advanced', icon: Settings2 },
]

function currentPathname() {
  return typeof window === 'undefined' ? '/' : window.location.pathname
}

function titleFromSlug(slug: string) {
  return slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function projectNavigationFromDocument(slug: string) {
  if (typeof document === 'undefined') return null
  const markers = document.querySelectorAll<HTMLElement>('[data-portal-project-slug]')
  for (const marker of markers) {
    if (marker.dataset.portalProjectSlug === slug) {
      return {
        name: marker.dataset.portalProjectName || titleFromSlug(slug),
        canManage: marker.dataset.portalProjectManage === 'true',
      }
    }
  }
  return null
}

function projectItems(slug: string, canManage: boolean): SidebarItem[] {
  const base = `/projects/${encodeURIComponent(slug)}`
  const items: SidebarItem[] = [
    { label: 'Overview', href: base, icon: CircleGauge },
    { label: 'Deployments', href: `${base}/deployments`, icon: CloudCog },
    { label: 'Workflows', href: `${base}/workflows`, icon: Workflow },
    { label: 'Agents', href: `${base}/agents`, icon: Bot },
    { label: 'Applications', href: `${base}/applications`, icon: AppWindow },
    { label: 'Usage', href: `${base}/usage`, icon: BarChart3 },
    { label: 'Previews', href: `${base}/previews`, icon: Gauge },
  ]
  if (canManage) {
    items.push(
      { label: 'General', href: `${base}/settings`, icon: Settings2, sectionLabel: 'Settings' },
      { label: 'Git', href: `${base}/git`, icon: FolderGit2 },
      { label: 'Environments', href: `${base}/environments`, icon: Gauge, match: 'prefix' },
      { label: 'Variables', href: `${base}/variables`, icon: KeyRound },
      { label: 'Firewall', href: `${base}/firewall`, icon: Shield },
      { label: 'Advanced', href: `${base}/advanced`, icon: Settings2 },
    )
  }
  return items
}

function teamItems(id: string): SidebarItem[] {
  const base = `/organization/teams/${encodeURIComponent(id)}`
  return [
    { label: 'Projects', href: base, icon: FolderKanban },
    { label: 'Deployments', href: `${base}/deployments`, icon: Activity },
    { label: 'Usage', href: `${base}/usage`, icon: BarChart3 },
    { label: 'Teams', href: `${base}/teams`, icon: Network },
    { label: 'Members', href: `${base}/access`, icon: UsersRound },
    { label: 'General', href: `${base}/general`, icon: Settings2, sectionLabel: 'Settings' },
    { label: 'Advanced', href: `${base}/advanced`, icon: Settings2 },
  ]
}

type NavigationContext =
  | { kind: 'workspace' }
  | { kind: 'organization' }
  | { kind: 'project'; slug: string }
  | { kind: 'environment'; projectSlug: string; envSlug: string }
  | { kind: 'team'; id: string }
  | { kind: 'member'; id: string }
  | { kind: 'none' }

function navigationContext(pathname: string): NavigationContext {
  const environmentMatch = pathname.match(/^\/projects\/([^/]+)\/environments\/([^/]+)/)
  if (environmentMatch?.[1] && environmentMatch[2]) {
    return { kind: 'environment', projectSlug: decodeURIComponent(environmentMatch[1]), envSlug: decodeURIComponent(environmentMatch[2]) }
  }
  const projectMatch = pathname.match(/^\/projects\/([^/]+)/)
  if (projectMatch?.[1]) {
    return { kind: 'project', slug: decodeURIComponent(projectMatch[1]) }
  }
  const memberMatch = pathname.match(/^\/members\/([^/]+)/)
  if (memberMatch?.[1]) {
    return { kind: 'member', id: decodeURIComponent(memberMatch[1]) }
  }
  const teamMatch = pathname.match(/^\/organization\/teams\/([^/]+)/)
  if (teamMatch?.[1]) {
    return { kind: 'team', id: decodeURIComponent(teamMatch[1]) }
  }
  if (pathname === '/organization' || pathname.startsWith('/organization/')) {
    return { kind: 'organization' }
  }
  if (pathname === '/' || pathname === '/deployments' || pathname === '/usage' || pathname === '/logs' || pathname === '/traces') {
    return { kind: 'workspace' }
  }
  return { kind: 'none' }
}

function environmentNavigationFromDocument(projectSlug: string, envSlug: string) {
  if (typeof document === 'undefined') return null
  const marker = document.querySelector<HTMLElement>('[data-portal-environment-slug]')
  if (!marker || marker.dataset.portalEnvironmentSlug !== envSlug || marker.dataset.portalEnvironmentProject !== projectSlug) return null
  return { name: marker.dataset.portalEnvironmentName || titleFromSlug(envSlug), isPreview: marker.dataset.portalEnvironmentPreview === 'true' }
}

function environmentItems(projectSlug: string, envSlug: string, isPreview: boolean): SidebarItem[] {
  const base = `/projects/${encodeURIComponent(projectSlug)}/environments/${encodeURIComponent(envSlug)}`
  const items: SidebarItem[] = [{ label: 'General', href: `${base}/general`, icon: Settings2, hardNavigation: true }]
  if (!isPreview) items.push({ label: 'Domains', href: `${base}/domains`, icon: Globe2, hardNavigation: true })
  return items
}

/**
 * Persistent navigation frame. It lives in the root app layout so soft
 * navigation swaps the page and menu context without remounting the shell.
 */
export function PortalNavigationClient({ children, shell }: { children: ReactNode; shell?: PortalShellData | undefined }) {
  const [pathname, setPathname] = useState(currentPathname)
  const context = useMemo<NavigationContext>(() => {
    if (!shell) return navigationContext(pathname)
    const routeContext = navigationContext(pathname)
    if (routeContext.kind === 'environment') return routeContext
    if (shell.contextKind === 'project') return { kind: 'project', slug: shell.contextId }
    if (shell.contextKind === 'team') return { kind: 'team', id: shell.contextId }
    if (shell.contextKind === 'member') return { kind: 'member', id: shell.contextId }
    if (shell.contextKind === 'organization') return { kind: 'organization' }
    if (shell.contextKind === 'workspace') return { kind: 'workspace' }
    return { kind: 'none' }
  }, [pathname, shell])
  const initialProjectNavigation = context.kind === 'project' ? projectNavigationFromDocument(context.slug) : null
  const projectName = shell?.contextName || initialProjectNavigation?.name || ''
  const teamName = shell?.contextName || ''
  const canManageProject = shell?.canManageContext ?? initialProjectNavigation?.canManage ?? false
  const isCurrentMember = shell?.isCurrentMember ?? false
  const organizationName = shell?.organizationName || 'Organization'
  const canViewSettings = shell?.canViewSettings ?? false
  const settingsHref = shell?.settingsHref || '/organization'

  useEffect(() => {
    const update = () => setPathname(currentPathname())
    const unsubscribe = subscribeNavigation((event) => {
      if (event.type === 'success') update()
    })
    window.addEventListener('popstate', update)
    return () => {
      unsubscribe()
      window.removeEventListener('popstate', update)
    }
  }, [])

  if (context.kind === 'none') return children

  if (context.kind === 'environment') {
    const navigation = environmentNavigationFromDocument(context.projectSlug, context.envSlug)
    return (
      <SidebarLayout
        title={navigation?.name || titleFromSlug(context.envSlug)}
        titleIcon={Gauge}
        items={environmentItems(context.projectSlug, context.envSlug, navigation?.isPreview || context.envSlug === 'preview')}
        backHref={`/projects/${encodeURIComponent(context.projectSlug)}/environments`}
        backLabel="Back to environments"
      >
        {children}
      </SidebarLayout>
    )
  }

  if (context.kind === 'project') {
    return (
      <SidebarLayout
        title={projectName || titleFromSlug(context.slug)}
        titleIcon={FolderKanban}
        items={projectItems(context.slug, canManageProject)}
        backHref="/"
        backLabel="Back to projects"
      >
        {children}
      </SidebarLayout>
    )
  }

  if (context.kind === 'team') {
    return (
      <SidebarLayout
        title={teamName || 'Team'}
        titleIcon={Network}
        items={teamItems(context.id)}
        backHref="/organization/teams"
        backLabel="Back to teams"
      >
        {children}
      </SidebarLayout>
    )
  }

  if (context.kind === 'member') {
    const base = `/members/${encodeURIComponent(context.id)}`
    const items: SidebarItem[] = [
      { label: 'Profile', href: base, icon: UserRound },
    ]
    if (isCurrentMember) {
      items.push(
        { label: 'Organizations', href: `${base}/organizations`, icon: Building2 },
        { label: 'Security', href: `${base}/security`, icon: ShieldCheck },
        { label: 'Teams', href: `${base}/teams`, icon: Network, sectionLabel: organizationName },
      )
    }
    return (
      <SidebarLayout
        title={isCurrentMember ? 'Your profile' : 'Member'}
        titleIcon={UserRound}
        items={items}
        backHref="/organization/members"
        backLabel="Back to members"
      >
        {children}
      </SidebarLayout>
    )
  }

  if (context.kind === 'organization') {
    return (
      <SidebarLayout
        title={organizationName}
        titleIcon={Building2}
        items={organizationItems}
        backHref="/"
        backLabel="Back to projects"
      >
        {children}
      </SidebarLayout>
    )
  }

  return (
    <SidebarLayout
      title={organizationName}
      titleIcon={Building2}
      items={organizationItems}
    >
      {children}
    </SidebarLayout>
  )
}
